/** Pure sidebar ordering + meta-line logic, split out from app.ts so it can be
 *  unit-tested without a DOM. */

import { t, intlLocale } from "./i18n.js";
import { formatPublishAt } from "./date-utils.js";

/** Statuses that aren't "live correspondence" yet — they show a status marker
 *  instead of a publication date. `grace` is grouped with setup: like setup it
 *  precedes the first turn, and `is_my_turn` is never computed for it (api.py
 *  gates that on status === "active"), so it must not pretend to be live. */
const PRELIVE_STATUS: Record<string, string> = {
  setup: "app.status.setup",
  grace: "app.status.setup",
  paused: "app.status.paused",
  closed: "app.status.closed",
};

/** Sidebar meta line. On the viewer's turn, shows their own deadline (the thing
 *  they need to act on) instead of the last publication — otherwise an active
 *  group shows its last publication date in the viewer's local time, no
 *  role/position. Pre-live groups (setup/grace/paused/closed) show a status
 *  marker (a date would be absent or misleading). */
export function metaLabel(g: any): string {
  const marker = PRELIVE_STATUS[g.status];
  if (marker) return t(marker);
  if (g.is_my_turn) return formatPublishAt(g.next_publication_slot, true);
  if (!g.last_publication_at) return t("app.never_published");
  const when = new Date(g.last_publication_at).toLocaleDateString(intlLocale(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return t("app.last_published", { when });
}

/** Sort key for recency: epoch ms of the last publication, or -1 for never
 *  published (a finite sentinel, so two never-published groups subtract to 0 —
 *  not NaN — and fall back to the name tiebreak deterministically). */
function pubTime(g: any): number {
  return g.last_publication_at ? new Date(g.last_publication_at).getTime() : -1;
}

/** Order groups: those awaiting your letter first, then most recently published,
 *  then by name (stable, engine-independent tiebreak). Sorts in place and
 *  returns the array. */
export function sortGroups<T extends any[]>(groups: T): T {
  groups.sort((a: any, b: any) => {
    if (a.is_my_turn !== b.is_my_turn) return a.is_my_turn ? -1 : 1;
    const byTime = pubTime(b) - pubTime(a);
    if (byTime !== 0) return byTime;
    return String(a.name).localeCompare(String(b.name));
  });
  return groups;
}
